import { useEffect, useState } from 'react';
import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { GridInitialState } from '@mui/x-data-grid-pro';
import { Run, RunStatus, fetchRuns, getRunsSubject } from '../../../store/Run';
import { getCurrentUser, UserRole } from '../../../store/User';
import { getCustomizationSubject, getDefaultPreferences, GridPreferences } from '../../../store/UserCustomizations';
import { DefaultRunGridColumnDefs } from './RunGridColumnDefs';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { getInitialState } from '../../Compounds/UpstackDataGrid/helpers';
import { PageCard } from '../../Compounds/CardWithTitle';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { EditRunDialog } from './EditRunDialog';
import { SetActiveRunDialog } from './SetActiveRunDialog';
import { TextSpan } from '../../Atoms/Text';
import { Icon } from '../../Atoms/Icon';

export const CalculatedRunTab = () => {
  const [runsLoading, setRunsLoading] = useState<boolean>(false);
  const [runs, setRuns] = useState<Run[]>([]);
  const [currentEditRun, setCurrentEditRun] = useState<Run | null>(null);
  const [isActiveDialogOpen, setIsActiveDialogOpen] = useState<boolean>(false);
  const [gridSettings, setGridSettings] = useState<GridPreferences>(
    getDefaultPreferences()?.content['calculatedRunGrid'] as GridPreferences
  );
  const [initialState, setInitialState] = useState<GridInitialState>();
  const columns = [...DefaultRunGridColumnDefs];

  const currentUser = getCurrentUser();
  const isAdminOrEngineer = currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.ENGINEER;

  if (isAdminOrEngineer) {
    columns.push({
      field: 'actions',
      headerName: 'Actions',
      width: 70,
      type: 'singleSelect',
      valueOptions: [],
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: { row: Run }) =>
        (params.row.status === RunStatus.PROCESSING || params.row.status === RunStatus.PENDING_LOCK) && (
          <IconButton
            sx={{
              color: 'rgb(107 114 128)'
            }}
            size="small"
            onClick={() => setCurrentEditRun(params.row)}>
            <EditIcon />
          </IconButton>
        )
    });
  }
  const filterKeys = columns.filter((column) => column.filterable).map((column) => column.field);

  const handleFetchRuns = async (localLoading?: boolean) => {
    if (localLoading) setRunsLoading(true);
    await fetchRuns();
    if (localLoading) setRunsLoading(false);
  };

  useEffect(() => {
    const runsSub = getRunsSubject().subscribe((runs) => {
      const activeStatuses = [RunStatus.PROCESSING, RunStatus.PENDING_LOCK, RunStatus.LOCKED];
      const filteredRuns = runs.filter((r) => activeStatuses.includes(r.status));
      setRuns(filteredRuns);
    });
    const userCustSub = getCustomizationSubject().subscribe((userCust) => {
      if (userCust) {
        setGridSettings(userCust.content.calculatedRunGrid);
        const state = getInitialState(userCust.content.calculatedRunGrid, columns, 'calculatedRunGrid');
        setInitialState(state);
        handleFetchRuns(true);
      }
    });

    return () => {
      if (runsSub) runsSub.unsubscribe();
      if (userCustSub) userCustSub.unsubscribe();
    };
  }, []);

  const cardActions = () =>
    isAdminOrEngineer ? (
      <div className="space-x-6 flex">
        <button
          className="flex items-center space-x-2"
          onClick={() => setIsActiveDialogOpen(true)}
          data-cy="activate-run-button"
          data-testid="activate-run-button">
          <Icon type="edit" />
          <TextSpan
            color="indigo"
            size="sm14">
            Set Active Run
          </TextSpan>
        </button>
      </div>
    ) : undefined;

  return (
    <PageCard
      dataTestId="calculated-run-management"
      dataCy="calculated-run-management"
      childrenWrapperClass="min-h-80 max-w-[60vw] min-w-full"
      title="Runs"
      cardActions={cardActions()}>
      <>
        {(!initialState || runsLoading) && (
          <div className="flex h-[45rem] m-auto">
            <ComponentSpinner label="Loading data..." />
          </div>
        )}
        {initialState && !runsLoading && (
          <UpstackDataGrid
            title=""
            rows={runs}
            columns={columns}
            page="calculatedRunGrid"
            filterKeys={filterKeys}
            gridSettings={gridSettings}
            showSearch={false}
            initialState={initialState}
            apiFilter={false}
            sxStyles={{ borderWidth: '0', borderBottomLeftRadius: '.75rem', borderBottomRightRadius: '.75rem' }}
          />
        )}
      </>
      <EditRunDialog
        open={currentEditRun !== null}
        run={currentEditRun}
        onClose={() => setCurrentEditRun(null)}
      />
      <SetActiveRunDialog
        open={isActiveDialogOpen}
        onClose={() => setIsActiveDialogOpen(false)}
      />
    </PageCard>
  );
};
